import { ColumnEditorOptions } from "primereact/column";
import { InputNumber, InputNumberValueChangeEvent } from "primereact/inputnumber";
import { useState } from "react";

interface NumberEditorProps {
    options: ColumnEditorOptions;
    /** The question's numeric type: `integer` pins the input to whole numbers,
     *  `percentage` supplies the % suffix when the question declares no unit. */
    questionType?: string | null;
    unit?: string;
    min?: number;
    max?: number;
    /** Fraction digits the data dictionary allows; ignored for `integer`. */
    decimalPlaces?: number;
}

const toNumber = (value: unknown): number | null => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
        const parsed = parseFloat(value);
        return isNaN(parsed) ? null : parsed;
    }
    return null;
};

export const NumberEditor = ({ options, questionType, unit, min, max, decimalPlaces }: NumberEditorProps) => {
    // Seed once so the existing number shows on open and survives re-renders.
    const [value, setValue] = useState<number | null>(() => toNumber(options.value));

    // An integer answer takes no fraction digits at all; anything else honours
    // the dictionary's decimalPlaces, keeping the previous allowance when it
    // sets none.
    const maxFractionDigits = questionType === "integer" ? 0 : decimalPlaces ?? 4;
    const displayUnit = unit || (questionType === "percentage" ? "%" : undefined);

    const handleChange = (e: InputNumberValueChangeEvent) => {
        setValue(e.value ?? null);
        options.editorCallback?.(e.value);
    };

    return (
        <div className="flex align-items-center gap-2">
            <InputNumber
                value={value}
                onValueChange={handleChange}
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={maxFractionDigits}
                min={min}
                max={max}
                autoFocus
            />
            {displayUnit && <span className="text-sm text-color-secondary">{displayUnit}</span>}
        </div>
    );
};

export const numberDisplayTemplate = (value: unknown): string => {
    if (value === null || value === undefined || value === "") return "";

    const numValue = typeof value === "string" ? parseFloat(value) : value;
    if (typeof numValue !== "number" || isNaN(numValue)) return String(value);

    return String(numValue);
};
